import {Localize} from "@dp-instant/i18n";
import {ComponentClassName} from "@dp-instant/ui";
import {SVG} from "@game/ui/images/";
import {IClickableComponentProps} from "@game/ui/interface/IClickableComponentProps";
import cn from "classnames";
import {getFontClassByDigits} from "@game/ui/uiHelpers";
import "./Booster.less";
import {playUIClickSound} from "@game/helpers";
import {hapticMedium} from "@game/haptic";

export enum BoosterType {
    START_UNITS = "BoosterType.START_UNITS",
    START_PRODUCE = "BoosterType.START_PRODUCE",
    OFFLINE_EARNINGS = "BoosterType.OFFLINE_EARNINGS"
}

interface IBoosterProps extends IClickableComponentProps, ComponentClassName {
    id: BoosterType;
    title?: any;
    disabled?: boolean;
    isFree?: boolean;
    isGated?: boolean;
    gatePrice?: number;
    defaultCount?: string;
    description?: any;
    price?: number;
    levelNum?: number;
    onUnlock?: () => void;
    onInsufficientCoins?: (price: number) => void;
}

const boosterGraphicsHelper = (type: BoosterType, icon: boolean = false) => {
    switch (type) {
        case BoosterType.START_UNITS:
            return icon ? <SVG.BoosterStartUnitsIcon /> : <SVG.BoosterStartUnits />;
        case BoosterType.START_PRODUCE:
            return icon ? <SVG.BoosterProduceSpeedIcon /> : <SVG.BoosterProduceSpeed />;
        default:
            return icon ? <SVG.BoosterOfflineEarningsIcon /> : <SVG.COINS />;
    }
};

export const Booster = ({
    id,
    title,
    disabled = true,
    isFree = false,
    isGated = false,
    gatePrice = 0,
    defaultCount = "10",
    description,
    price = 50,
    onClick,
    onUnlock,
    onInsufficientCoins,
    className,
    levelNum = 10
}: IBoosterProps) => (
    //@ts-ignore
    <div disabled={disabled} className={cn("booster", className, {disabled: disabled})}>
        <div className={cn("booster-head")}>
            <div className={cn("booster-head__icon")}>{boosterGraphicsHelper(id, true)}</div>
            <div className={cn("booster-head__info")}>
                <div
                    className={cn("booster-head__total", getFontClassByDigits(defaultCount, 2, 7))}
                >
                    {defaultCount}
                </div>
                <div className={cn("booster-head__description")}>
                    {Localize.get(description?.i18n, description?.default)}
                </div>
            </div>
        </div>
        <button
            onClick={() => {
                if (isGated && onUnlock) {
                    playUIClickSound();
                    hapticMedium();
                    onUnlock();
                    return;
                }
                if (disabled && onInsufficientCoins) {
                    playUIClickSound();
                    hapticMedium();
                    onInsufficientCoins(price);
                    return;
                }
                if (onClick) {
                    if (!disabled) {
                        playUIClickSound();
                        hapticMedium();
                    }
                    onClick();
                }
            }}
            disabled={false}
            className={cn("booster-body", "btn", {"booster-body_free": isFree, "booster-body_gated": isGated, "booster-body_disabled": disabled && !isGated})}
        >
            <div className={cn("booster-body__title")}>
                {Localize.get(title?.i18n, title?.default)}
            </div>
            <div className={cn("booster-body__image")}>{boosterGraphicsHelper(id)}</div>
            <div className={cn("booster-body__level", getFontClassByDigits(levelNum, 1, 3))}>
                {Localize.get("ui-menu-lvl", "LVL")} {levelNum}
            </div>
            <div className={cn("booster-body__price")}>
                {isGated ? (
                    <>
                        <div className={cn("booster-body__price-free")}>
                            🔒 {gatePrice} ⭐
                        </div>
                    </>
                ) : isFree ? (
                    <>
                        <div className={cn("booster-body__price-free")}>
                            {Localize.get("ui-common-free", "FREE")}
                        </div>
                        <div className={cn("booster-body__price-icon-free")}>
                            <SVG.Video />
                        </div>
                    </>
                ) : (
                    <>
                        <div
                            className={cn(
                                "booster-body__price-total",
                                getFontClassByDigits(price, 2, 7)
                            )}
                        >
                            {price}
                        </div>
                        <div className={cn("booster-body__price-icon")}>
                            <SVG.COINS />
                        </div>
                    </>
                )}
            </div>
        </button>
    </div>
);
